import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, X, MapPin } from "lucide-react";
import { toast } from "react-toastify";
import {
  getCities,
  addCity,
  updateCity,
  deleteCity,
} from "../../../services/api";
import "./CitiesManagement.css";

const empty = { name: "", state: "", rera: "" };

export default function Cities() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await getCities();
      setItems(list || []);
    } catch (err) {
      toast.error(err.message || "Failed to load cities");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  function validate() {
    const map = {};
    if (!form.name.trim()) map.name = "City name is required";
    if (!form.state.trim()) map.state = "State is required";
    setErrors(map);
    return Object.keys(map).length === 0;
  }

  async function submit(e) {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      if (editing) {
        const res = await updateCity(editing, form);
        toast.success(res.message || "City updated");
      } else {
        const res = await addCity(form);
        toast.success(res.message || "City added");
      }
      setForm(empty);
      setEditing(null);
      setErrors({});
      await refresh();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id) {
    if (!window.confirm("Delete this city?")) return;
    try {
      await deleteCity(id);
      toast.success("City deleted");
      await refresh();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  }

  function onEdit(c) {
    setEditing(c.id);
    setForm({
      name: c.name || c.cityname || "",
      state: c.state || "",
      rera: c.rera || "",
    });
    setErrors({});
  }

  function onCancel() {
    setEditing(null);
    setForm(empty);
    setErrors({});
  }

  return (
    <div className="mst">
      <div className="mst-head">
        <div>
          <div className="mst-eyebrow">Master data</div>
          <h1 className="mst-title">Cities</h1>
          <p className="mst-sub">
            Cities powering navigation, filters and listings — {items.length}{" "}
            total.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mst-card">
        <div className="mst-card-head">
          {editing ? "Edit city" : "Add new city"}
        </div>

        <div className="mst-grid-3">
          <div>
            <label className="mst-label">City name</label>
            <input
              className="mst-input"
              placeholder="Pune"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={saving}
            />
            {errors.name && <p className="mst-err">{errors.name}</p>}
          </div>

          <div>
            <label className="mst-label">State</label>
            <input
              className="mst-input"
              placeholder="Maharashtra"
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
              disabled={saving}
            />
            {errors.state && <p className="mst-err">{errors.state}</p>}
          </div>

          <div>
            <label className="mst-label">RERA info</label>
            <input
              className="mst-input"
              placeholder="MahaRERA registration"
              value={form.rera}
              onChange={(e) => setForm({ ...form, rera: e.target.value })}
              disabled={saving}
            />
          </div>
        </div>

        <div className="mst-actions">
          <button type="submit" className="mst-btn" disabled={saving}>
            <Plus className="h-4 w-4" />
            {saving
              ? "Saving…"
              : editing
              ? "Update city"
              : "Add city"}
          </button>
          {editing && (
            <button
              type="button"
              className="mst-btn-ghost"
              onClick={onCancel}
              disabled={saving}
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="mst-table-wrap">
        <table className="mst-table">
          <thead>
            <tr>
              <th>City</th>
              <th>State</th>
              <th>RERA info</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="mst-empty">
                  Loading cities…
                </td>
              </tr>
            )}

            {!loading &&
              items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="mst-city-name">
                      <MapPin className="h-4 w-4 mst-ico" />
                      {c.name}
                    </div>
                  </td>
                  <td>{c.state || "—"}</td>
                  <td>{c.rera || "—"}</td>
                  <td>
                    <div className="mst-row-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit"
                        onClick={() => onEdit(c)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn--danger"
                        title="Delete"
                        onClick={() => onDelete(c.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={4} className="mst-empty">
                  No cities added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}